/**
 * Hook personalizado para autoguardado de borradores de contratación
 * Fecha: Enero 2025
 * 
 * Características:
 * - Autoguardado con debounce (5 segundos)
 * - Guardado silencioso en backend
 * - Toast discreto al guardar exitosamente
 * - Fallback a localStorage en caso de error
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { hireDraftService } from '@/features/rh/api/hireDraft.service';
import type { HireEmployeeDto } from '../types/employee.types';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseHireDraftAutosaveOptions {
  candidateId: string;
  enabled?: boolean;
  debounceMs?: number;
  onSaveSuccess?: () => void;
  onSaveError?: (error: any) => void;
}

interface UseHireDraftAutosaveReturn {
  saveStatus: SaveStatus;
  lastSaved: Date | null;
  saveDraft: (data: Partial<HireEmployeeDto>) => void;
  deleteDraft: () => Promise<void>;
  loadDraft: () => Promise<Partial<HireEmployeeDto> | null>;
}

/**
 * Hook para autoguardado de borradores
 */
export const useHireDraftAutosave = ({
  candidateId,
  enabled = true,
  debounceMs = 3000,
  onSaveSuccess,
  onSaveError,
}: UseHireDraftAutosaveOptions): UseHireDraftAutosaveReturn => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getLocalStorageKey = useCallback(() => {
    return `hire_form_draft_${candidateId}`;
  }, [candidateId]);

  const saveToLocalStorage = useCallback((data: Partial<HireEmployeeDto>) => {
    try {
      localStorage.setItem(getLocalStorageKey(), JSON.stringify({
        formData: data,
        timestamp: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('❌ Error al guardar en localStorage:', error);
    }
  }, [getLocalStorageKey]);

  const loadFromLocalStorage = useCallback((): Partial<HireEmployeeDto> | null => {
    try {
      const saved = localStorage.getItem(getLocalStorageKey());
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.formData;
      }
    } catch (error) {
      console.error('❌ Error al cargar desde localStorage:', error);
    }
    return null;
  }, [getLocalStorageKey]);

  const cleanDraftData = useCallback((data: Partial<HireEmployeeDto>): Partial<HireEmployeeDto> => {
    const cleaned: any = {};
    
    Object.entries(data).forEach(([key, value]) => {
      // Solo incluir campos con valores válidos
      if (value !== '' && value !== null && value !== undefined && value !== 0) {
        cleaned[key] = value;
      }
    });
    
    return cleaned;
  }, []);

  const saveDraftInternal = useCallback(async (data: Partial<HireEmployeeDto>) => {
    if (!enabled) return;

    try {
      setSaveStatus('saving');
      const cleanedData = cleanDraftData(data);
      
      // Solo enviar al backend si hay datos válidos
      if (Object.keys(cleanedData).length > 0) {
        await hireDraftService.saveDraft(candidateId, cleanedData);
      }
      
      saveToLocalStorage(data);
      setSaveStatus('saved');
      setLastSaved(new Date());
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('❌ Error al guardar borrador:', error);
      saveToLocalStorage(data);
      setSaveStatus('error');
      
      if (onSaveError) {
        onSaveError(error);
      }
      
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [candidateId, enabled, saveToLocalStorage, onSaveSuccess, onSaveError, cleanDraftData]);

  const saveDraft = useCallback((data: Partial<HireEmployeeDto>) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveDraftInternal(data);
    }, debounceMs);
  }, [saveDraftInternal, debounceMs]);

  const loadDraft = useCallback(async (): Promise<Partial<HireEmployeeDto> | null> => {
    try {
      const draft = await hireDraftService.getDraft(candidateId);
      
      if (draft) {
        setLastSaved(new Date(draft.updatedAt));
        return draft.draftData;
      }
      
      const localDraft = loadFromLocalStorage();
      if (localDraft) {
        return localDraft;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error al cargar borrador:', error);
      return loadFromLocalStorage();
    }
  }, [candidateId, loadFromLocalStorage]);

  const deleteDraft = useCallback(async () => {
    try {
      await hireDraftService.deleteDraft(candidateId);
    } catch (error) {
      console.error('❌ Error al eliminar borrador del backend:', error);
    }
    
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (error) {
      console.error('❌ Error al eliminar de localStorage:', error);
    }
    
    setSaveStatus('idle');
    setLastSaved(null);
  }, [candidateId, getLocalStorageKey]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Si se deshabilita el autoguardado (ej. step final), cancelar timeout pendiente
  useEffect(() => {
    if (!enabled && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, [enabled]);

  return {
    saveStatus,
    lastSaved,
    saveDraft,
    deleteDraft,
    loadDraft,
  };
};
